import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import BasePageLayout from '../../components/BasePageLayout';
import CustomDropdown, { DropdownOption } from '../../components/ui/CustomDropdown';
import { useAuth } from '../../context/AuthContext';
import { useCountry } from '../../context/CountryContext';
import { useLanguage } from '../../context/LanguageContext';
import { useDeviceType, DeviceType } from '../../hooks/useDeviceType';
import { Questionnaire, CompletedQuestionnaire } from '../../types/questionnaire';
import { QuestionnaireService } from '../../services/questionnaireService';
import '../../styles/shared/dashboard.css';
import '../../styles/pages/QuestionnairePages.css';
import '../../styles/pages/QuestionnaireHubModern.css';
import '../../styles/pages/CompactQuestionnaireHeader.css';

type TakerTab = 'available' | 'completed';

const QuestionnaireTakerPage: React.FC = () => {
  const [rawAvailableQuestionnaires, setRawAvailableQuestionnaires] = useState<Questionnaire[]>([]);
  const [rawCompletedQuestionnaires, setRawCompletedQuestionnaires] = useState<CompletedQuestionnaire[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<'newest' | 'points' | 'category'>('newest');
  const [categories, setCategories] = useState<string[]>([]);
  const [activeTab, setActiveTab] = useState<TakerTab>('available');
  const [selectedQuestionnaire, setSelectedQuestionnaire] = useState<Questionnaire | CompletedQuestionnaire | null>(null);
  // Questionnaire detail panel state
  const [showQuestionnaireDetail, setShowQuestionnaireDetail] = useState(false);

  // Responsive state
  const [showMobileView, setShowMobileView] = useState(false);
  const [isCompactView, setIsCompactView] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [questionnairesPerPage] = useState(10);

  const [stats, setStats] = useState({
    available: 0,
    completed: 0,
    totalEarnings: 0
  });

  const { user } = useAuth();
  const { selectedCountry } = useCountry();
  const { t } = useLanguage();
  const { deviceType } = useDeviceType();

  // Dropdown options
  const categoryOptions: DropdownOption[] = useMemo(() => [
    { value: 'all', label: t('questionnaires.allSkills') },
    { value: 'Programming', label: t('questionnaires.programming') },
    { value: 'Design', label: t('questionnaires.design') },
    { value: 'Marketing', label: t('questionnaires.marketing') },
    { value: 'Research', label: t('questionnaires.research') }
  ], [t]);

  const sortOptions: DropdownOption[] = useMemo(() => [
    { value: 'newest', label: t('questionnaires.newestFirst') },
    { value: 'points', label: t('questionnaires.highestPoints') },
    { value: 'category', label: t('questionnaires.category') }
  ], [t]);

  // Set responsive states based on device type
  useEffect(() => {
    if (!deviceType) return;

    const isMobile = deviceType === DeviceType.MOBILE;
    const isTablet = deviceType === DeviceType.TABLET;

    setIsCompactView(isMobile || isTablet);
    setShowMobileView(isMobile || isTablet);
  }, [deviceType]);

  // Taker Type Buttons Configuration
  const takerTypeButtons = useMemo(() => [
    {
      id: 'available' as TakerTab,
      label: t('questionnaires.available'),
      hint: t('questionnaires.hintAvailable'),
      icon: 'bi-play-circle',
      count: stats.available,
      gradient: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)'
    },
    {
      id: 'completed' as TakerTab,
      label: t('questionnaires.completed'),
      hint: t('questionnaires.hintCompleted'),
      icon: 'bi-check-circle',
      count: stats.completed,
      gradient: 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
    }
  ], [t, stats.available, stats.completed]);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Function to refetch and update questionnaire data
  const refetchQuestionnaireData = useCallback(async () => {
    if (!user) return;

    try {
      // Execute all calls in parallel for better performance
      const [
        availableResult,
        completedResult
      ] = await Promise.all([
        QuestionnaireService.getAvailableQuestionnaires(user.id, selectedCountry),
        QuestionnaireService.getMyCompletedQuestionnaires()
      ]);

      // Check for errors from parallel calls
      if (availableResult.error) throw availableResult.error;
      if (completedResult.error) throw completedResult.error;

      // Extract data from results
      const availableData = availableResult.questionnaires || [];
      const completedData = completedResult.questionnaires || [];

      console.log('📋 Available questionnaires data:', availableData);
      console.log('📋 Completed questionnaires data:', completedData);

      // Update state
      setRawAvailableQuestionnaires(availableData);
      setRawCompletedQuestionnaires(completedData);

      // Calculate stats
      const totalEarnings = completedData.reduce((sum, q) => sum + (q.reward_points || 0), 0);

      setStats({
        available: availableData.length,
        completed: completedData.length,
        totalEarnings: totalEarnings
      });

      // Extract categories from all questionnaire data
      const categorySet = new Set<string>();
      [...availableData, ...completedData].forEach(questionnaire => {
        if (questionnaire.skill_required) categorySet.add(questionnaire.skill_required);
      });
      setCategories(Array.from(categorySet));

      console.log('📊 Taker stats:', {
        available: availableData.length,
        completed: completedData.length,
        totalEarnings: totalEarnings
      });

    } catch (err) {
      console.error('Error fetching questionnaires:', err);
      setError('Failed to load questionnaires. Please try again later.');
    }
  }, [user, selectedCountry]);

  // Fetch data on component mount
  useEffect(() => {
    const fetchQuestionnaires = async () => {
      if (!user) return;

      try {
        setLoading(true);
        setError(null);

        // Use the refetchQuestionnaireData function for initial load
        await refetchQuestionnaireData();

      } catch (err) {
        console.error('Error fetching questionnaires:', err);
        setError('Failed to load questionnaires. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    // Only fetch on user changes
    fetchQuestionnaires();
  }, [user?.id, refetchQuestionnaireData]);

  // Sort function
  const sortQuestionnaires = useCallback((questionnaires: (Questionnaire | CompletedQuestionnaire)[]) => {
    const sortedQuestionnaires = [...questionnaires];
    
    switch (sortBy) {
      case 'newest':
        return sortedQuestionnaires.sort((a, b) => new Date(b.created_at || '').getTime() - new Date(a.created_at || '').getTime());
      case 'points':
        return sortedQuestionnaires.sort((a, b) => (b.reward_points || 0) - (a.reward_points || 0));
      case 'category':
        return sortedQuestionnaires.sort((a, b) => (a.skill_required || '').localeCompare(b.skill_required || ''));
      default:
        return sortedQuestionnaires;
    }
  }, [sortBy]);

  // Filter questionnaires based on search and filters
  const getFilteredQuestionnaires = useCallback((questionnaires: (Questionnaire | CompletedQuestionnaire)[]) => {
    const filtered = questionnaires.filter(questionnaire => {
      const matchesSearch = !debouncedSearchTerm ||
        questionnaire.title.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
        (questionnaire.description && questionnaire.description.toLowerCase().includes(debouncedSearchTerm.toLowerCase()));

      const matchesCategory = categoryFilter === 'all' || questionnaire.skill_required === categoryFilter;

      return matchesSearch && matchesCategory;
    });

    return sortQuestionnaires(filtered);
  }, [debouncedSearchTerm, categoryFilter, sortQuestionnaires]);

  // Get current questionnaires based on active tab
  const getCurrentQuestionnaires = useMemo(() => {
    if (activeTab === 'available') {
      return getFilteredQuestionnaires(rawAvailableQuestionnaires);
    } else {
      return getFilteredQuestionnaires(rawCompletedQuestionnaires);
    }
  }, [activeTab, rawAvailableQuestionnaires, rawCompletedQuestionnaires, getFilteredQuestionnaires]);

  // Pagination
  const totalPages = Math.ceil(getCurrentQuestionnaires.length / questionnairesPerPage);
  const currentQuestionnaires = getCurrentQuestionnaires.slice(
    (currentPage - 1) * questionnairesPerPage,
    currentPage * questionnairesPerPage
  );

  const handleTabSwitch = useCallback((tab: TakerTab) => {
    setActiveTab(tab);
    setCurrentPage(1);
    setSelectedQuestionnaire(null);
    setShowQuestionnaireDetail(false);
  }, []);

  const handleQuestionnaireSelect = useCallback((questionnaire: Questionnaire | CompletedQuestionnaire) => {
    // Keep the questionnaire visible in the list when selected
    setSelectedQuestionnaire(prevSelected => prevSelected?.id === questionnaire.id ? prevSelected : questionnaire);
    setShowQuestionnaireDetail(true);
  }, []);

  // Preserve selected questionnaire after data refresh by finding it in the updated lists
  useEffect(() => {
    if (selectedQuestionnaire) {
      // Find the selected questionnaire in the updated data
      const updatedQuestionnaire = activeTab === 'available' 
        ? rawAvailableQuestionnaires.find(q => q.id === selectedQuestionnaire.id)
        : rawCompletedQuestionnaires.find(q => q.id === selectedQuestionnaire.id);
      
      // Update selectedQuestionnaire with fresh data if found, otherwise keep the current one
      if (updatedQuestionnaire && JSON.stringify(updatedQuestionnaire) !== JSON.stringify(selectedQuestionnaire)) {
        setSelectedQuestionnaire(updatedQuestionnaire);
      }
    }
  }, [rawAvailableQuestionnaires, rawCompletedQuestionnaires, selectedQuestionnaire, activeTab]);

  if (loading) {
    return (
      <BasePageLayout background="white" padding="none" maxWidth="none">
        <div className="loading-container">
          <div className="loading-ring"></div>
          <p>{t('questionnaires.loadingQuestionnaires')}</p>
        </div>
      </BasePageLayout>
    );
  }

  if (error) {
    return (
      <BasePageLayout background="white" padding="none" maxWidth="none">
        <div className="error-container">
          <h3>{t('common.error')}</h3>
          <p>{error}</p>
        </div>
      </BasePageLayout>
    );
  }

  return (
    <BasePageLayout background="white" padding="none" maxWidth="none">
      <div className="questionnaire-hub-container">
        {/* Compact Header */}
        <div className="compact-questionnaire-header">
          <div className="compact-header-content">
            {/* Title Section */}
            <div className="compact-title-section">
              <div>
                <h1 className="compact-page-title">{t('questionnaires.questionnaireHub')}</h1>
                <div className="compact-breadcrumb">
                  <span>{t('navigation.questionnaires')}</span>
                  <i className="bi bi-chevron-right compact-breadcrumb-separator"></i>
                  <span>{t('navigation.takeSurveys')}</span>
                </div>
              </div>
              <div className="compact-stats-mini">
                <div className="stat-item-mini">
                  <span className="stat-number-mini">{stats.available}</span>
                  <span className="stat-label-mini">{t('questionnaires.available')}</span>
                </div>
                <div className="stat-item-mini earnings">
                  <span className="stat-number-mini">{stats.totalEarnings}</span>
                  <span className="stat-label-mini">{t('questionnaires.pointsEarned')}</span>
                </div>
              </div>
            </div>

            {/* Controls Section */}
            <div className="compact-controls-section">
              {/* Taker Type Buttons */}
              <div className="compact-type-buttons">
                {takerTypeButtons.map(button => (
                  <button
                    key={button.id}
                    className={`compact-type-button compact-tooltip ${activeTab === button.id ? 'active' : ''} ${button.count === 0 ? 'disabled' : ''}`}
                    onClick={() => button.count > 0 && handleTabSwitch(button.id)}
                    disabled={button.count === 0}
                    data-tooltip={button.hint}
                    style={{
                      background: activeTab === button.id ? button.gradient : undefined
                    }}
                  >
                    <i className={`bi ${button.icon} compact-type-button-icon`}></i>
                    <span className="compact-type-button-label">{button.label}</span>
                    <span className="compact-type-button-count">{button.count}</span>
                  </button>
                ))}
              </div>

              {/* Search and Filters */}
              <div className="compact-search-filters">
                <div className="compact-search-bar">
                  <i className="bi bi-search compact-search-icon"></i>
                  <input
                    type="text"
                    className="compact-search-input"
                    placeholder={t('questionnaires.searchQuestionnaires')}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>

                <div className="compact-filter-dropdown">
                  <CustomDropdown
                    options={categoryOptions}
                    value={categoryFilter}
                    onChange={setCategoryFilter}
                    placeholder={t('questionnaires.category')}
                    size="small"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className={`questionnaire-hub-main-two-column ${showQuestionnaireDetail ? 'detail-panel-open' : ''}`}>
          {/* Questionnaire List */}
          <div className="questionnaire-hub-questionnaire-list-column">
            <div className="questionnaire-hub-questionnaire-list-header">
              <span className="questionnaire-hub-results-count">
                {getCurrentQuestionnaires.length === 1 
                  ? t('questionnaires.resultsCountSingular', { count: getCurrentQuestionnaires.length })
                  : t('questionnaires.resultsCount', { count: getCurrentQuestionnaires.length })
                }
              </span>
              <div className="questionnaire-hub-sort-controls">
                <button
                  onClick={refetchQuestionnaireData}
                  className="questionnaire-hub-refresh-button"
                  title={t('questionnaires.refresh')}
                  disabled={loading}
                >
                  <i className={`bi bi-arrow-clockwise ${loading ? 'spinning' : ''}`}></i>
                </button>
                <div className="compact-filter-dropdown">
                  <CustomDropdown
                    options={sortOptions}
                    value={sortBy}
                    onChange={(value) => setSortBy(value as 'newest' | 'points' | 'category')}
                    placeholder={t('questionnaires.sortBy')}
                    size="small"
                  />
                </div>
              </div>
            </div>

            <div className="questionnaire-hub-questionnaire-list-content">
              {/* Questionnaire Cards */}
              <div className="questionnaire-hub-questionnaire-cards">
                {currentQuestionnaires.length === 0 ? (
                  <div className="no-questionnaires-message">
                    <i className="bi bi-inbox"></i>
                    <h3>{t('questionnaires.noQuestionnairesFound')}</h3>
                    <p>
                      {activeTab === 'available'
                        ? t('questionnaires.noQuestionnairesAvailable')
                        : t('questionnaires.noCompletedYet')
                      }
                    </p>
                    {activeTab === 'available' && (
                      <p className="text-muted">
                        {t('questionnaires.adjustSearch')}
                      </p>
                    )}
                  </div>
                ) : (
                  currentQuestionnaires.map(questionnaire => {
                    const isCompleted = activeTab === 'completed';
                    const completedQuestionnaire = isCompleted ? questionnaire as CompletedQuestionnaire : null;

                    return (
                      <div
                        key={questionnaire.id}
                        className={`questionnaire-card-wrapper ${selectedQuestionnaire?.id === questionnaire.id ? 'selected' : ''}`}
                        onClick={() => handleQuestionnaireSelect(questionnaire)}
                      >
                        <div className="questionnaire-card questionnaire-card-modern taker-questionnaire-card">
                          <div className="questionnaire-header-row">
                            <h3 className="questionnaire-title">{questionnaire.title}</h3>
                            <div className="questionnaire-status-badges">
                              {/* {isCompleted ? (
                                <span className="questionnaire-status-badge completed">
                                  <i className="bi bi-check-circle-fill"></i>
                                  Completed
                                </span>
                              ) : (
                                <span className="questionnaire-status-badge available">
                                  <i className="bi bi-circle"></i>
                                  Available
                                </span>
                              )} */}
                              <span className="questionnaire-points">
                                <i className="bi bi-star-fill"></i>
                                {questionnaire.reward_points || 0}
                              </span>
                            </div>
                          </div>

                          <p className="questionnaire-description">
                            {questionnaire.description && questionnaire.description.trim() && questionnaire.description.length > 120
                              ? `${questionnaire.description.substring(0, 120)}...`
                              : questionnaire.description && questionnaire.description.trim() 
                                ? questionnaire.description 
                                : t('questionnaires.noDescriptionAvailable')}
                          </p>

                          <div className="questionnaire-meta">
                            <div className="questionnaire-meta-left">
                              <span className="questionnaire-category">
                                <i className="bi bi-tag"></i>
                                {questionnaire.skill_required || t('questionnaires.general')}
                              </span>
                              <span className="questionnaire-verification-info">
                                <i className="bi bi-list-ul"></i>
                                {t('questionnaires.questionsCount', { count: questionnaire.total_questions || 0 })}
                              </span>
                              {!isCompleted && questionnaire.max_participants && (
                                <span className="questionnaire-verification-info">
                                  <i className="bi bi-people"></i>
                                  {t('questionnaires.responsesCount', { 
                                    current: questionnaire.current_participants || 0, 
                                    max: questionnaire.max_participants 
                                  })}
                                </span>
                              )}
                            </div>
                            <div className="questionnaire-meta-right">
                              <span className="questionnaire-date">
                                {isCompleted && completedQuestionnaire?.submitted_at
                                  ? new Date(completedQuestionnaire.submitted_at).toLocaleDateString()
                                  : new Date(questionnaire.created_at || '').toLocaleDateString()
                                }
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="pagination-container">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                  <button
                    key={page}
                    className={`pagination-btn ${currentPage === page ? 'active' : ''}`}
                    onClick={() => setCurrentPage(page)}
                  >
                    {page}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Questionnaire Detail Overlay */}
          {showQuestionnaireDetail && (
            <div className="questionnaire-detail-overlay" onClick={() => setShowQuestionnaireDetail(false)} />
          )}

          {/* Questionnaire Detail Panel - slides from right */}
          <div className={`questionnaire-detail-panel ${showQuestionnaireDetail ? 'panel-open' : ''}`}>
            {selectedQuestionnaire ? (
              <div className="questionnaire-detail-content">
                <div className="questionnaire-detail-header">
                  <div className="questionnaire-detail-header-main">
                    <div className="questionnaire-detail-title-area">
                      <h1 className="questionnaire-detail-title">{selectedQuestionnaire.title}</h1>
                      <div className="questionnaire-detail-metadata">
                        <span className="questionnaire-detail-category-badge">
                          <i className="bi bi-tag-fill"></i>
                          {selectedQuestionnaire.skill_required || t('questionnaires.general')}
                        </span>
                        <span className="questionnaire-detail-points-badge">
                          <i className="bi bi-star-fill"></i>
                          {selectedQuestionnaire.reward_points || 0} {t('questionnaires.pts')}
                        </span>
                        <span className="questionnaire-detail-questions-badge">
                          <i className="bi bi-list-ul"></i>
                          {t('questionnaires.questionsCount', { count: selectedQuestionnaire.total_questions || 0 })}
                        </span>
                        {activeTab === 'available' && selectedQuestionnaire.max_participants && (
                          <span className="questionnaire-detail-participants-badge">
                            <i className="bi bi-people-fill"></i>
                            {t('questionnaires.responsesCount', { 
                              current: selectedQuestionnaire.current_participants || 0, 
                              max: selectedQuestionnaire.max_participants 
                            })}
                          </span>
                        )}
                        <span className={`questionnaire-detail-status-badge status-${activeTab === 'available' ? 'available' : 'completed'}`}>
                          <i className={`bi ${activeTab === 'available' ? 'bi-circle' : 'bi-check-circle'}`}></i>
                          {activeTab === 'available' ? t('questionnaires.available') : t('questionnaires.completed')}
                        </span>
                        <span className="questionnaire-detail-date-badge">
                          <i className="bi bi-calendar3"></i>
                          {activeTab === 'completed' && 'submitted_at' in selectedQuestionnaire
                            ? new Date(selectedQuestionnaire.submitted_at).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric'
                              })
                            : new Date(selectedQuestionnaire.created_at || '').toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric'
                              })
                          }
                        </span>
                      </div>
                    </div>
                    
                    <div className="questionnaire-detail-header-actions">
                      {activeTab === 'available' && (
                        <button 
                          className="questionnaire-detail-action-btn new-tab"
                          onClick={() => {
                            const questionnaireUrl = `/questionnaires/${selectedQuestionnaire.id}/take`;
                            window.open(questionnaireUrl, '_blank');
                          }}
                          title={t('questionnaires.openInNewTab')}
                        >
                          <i className="bi bi-box-arrow-up-right"></i>
                        </button>
                      )}
                      
                      <button 
                        className="close-questionnaire-detail-btn"
                        onClick={() => setShowQuestionnaireDetail(false)}
                        title={t('questionnaires.close')}
                      >
                        <i className="bi bi-x-lg"></i>
                      </button>
                    </div>
                  </div>
                </div>
                
                <div className="questionnaire-detail-content">
                  <div style={{ padding: '2rem', flex: 1, overflowY: 'auto' }}>
                    {/* Action buttons section */}
                    {activeTab === 'available' && (
                      <section className="questionnaire-details-section">
                        <div className="questionnaire-details-section-header">
                          <h2 className="questionnaire-details-section-title">
                            <i className="bi bi-play-circle"></i>
                            {t('questionnaires.actions')}
                          </h2>
                        </div>
                        <div className="questionnaire-details-section-content">
                          <div className="questionnaire-details-actions-grid">
                            <Link
                              to={`/questionnaires/${selectedQuestionnaire.id}/take`}
                              className="questionnaire-hub-action-button primary">
                              <i className="bi bi-play-fill"></i>
                              {t('questionnaires.startQuestionnaire')}
                            </Link>
                          </div>
                        </div>
                      </section>
                    )}

                    {/* Description */}
                    <section className="questionnaire-details-section">
                      <div className="questionnaire-details-section-header">
                        <h2 className="questionnaire-details-section-title">
                          <i className="bi bi-file-text"></i>
                          {t('questionnaires.description')}
                        </h2>
                      </div>
                      <div className="questionnaire-details-section-content">
                        <p className="questionnaire-details-description">
                          {selectedQuestionnaire.description || t('questionnaires.noDescriptionAvailable')}
                        </p>
                      </div>
                    </section>
                  </div>
                </div>
              </div>
            ) : null}
          </div>

        </div>
      </div>
    </BasePageLayout>
  );
};

export default QuestionnaireTakerPage;
